import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const USER_NAV = [
  { id: 'overview', icon: '📊', label: 'Overview' },
  { id: 'requests', icon: '📝', label: 'My Tasks' },
  { id: 'new-request', icon: '➕', label: 'New Task' },
  { id: 'notifications', icon: '🔔', label: 'Notifications' },
  { id: 'profile', icon: '👤', label: 'Profile' },
];

const ADMIN_NAV = [
  { id: 'overview', icon: '📊', label: 'Dashboard' },
  { id: 'requests', icon: '📋', label: 'All Tasks' },
  { id: 'users', icon: '👥', label: 'Users' },
  { id: 'create-user', icon: '➕', label: 'Create User' },
  { id: 'plans', icon: '💳', label: 'Plans' },
  { id: 'orders', icon: '💰', label: 'Orders' },
  { id: 'developers', icon: '👨‍💻', label: 'Developers' },
  { id: 'contact', icon: '📬', label: 'Enquiries' },
  { id: 'subadmins', icon: '🛡️', label: 'Sub-Admins', adminOnly: true },
];

export default function Sidebar({ active, onNavigate, mobileOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // RBAC Permission check
  const hasAccess = (item) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'subadmin') {
      if (item.adminOnly) return false;
      if (item.id === 'overview') return true;
      let perms = [];
      try { perms = typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions; } catch(e) {}
      if (!perms) return false;
      return perms.includes(item.id);
    }
    return USER_NAV.some(nav => nav.id === item.id);
  };

  const navItems = user?.role === 'admin' || user?.role === 'subadmin' ? ADMIN_NAV.filter(hasAccess) : USER_NAV;

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99 }}
          onClick={onClose}
        />
      )}

      <div className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="logo-icon">⚡</div>
          <span className="text-gradient">InfraVelox</span>
        </div>

        {/* Role badge */}
        <div style={{ padding: '12px 20px' }}>
          <div style={{
            background: user?.role === 'admin' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.15)',
            border: `1px solid ${user?.role === 'admin' ? 'rgba(245,158,11,0.3)' : 'rgba(99,102,241,0.3)'}`,
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 700,
            color: user?.role === 'admin' ? '#fbbf24' : 'var(--brand-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            display: 'inline-block',
          }}>
            {user?.role === 'admin' ? '⚡ Admin Panel' : user?.role === 'subadmin' ? '🛡️ Sub-Admin Panel' : '👤 User Dashboard'}
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-section">Navigation</div>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${active === item.id ? 'active' : ''}`}
              onClick={() => { onNavigate(item.id); if (onClose) onClose(); }}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
              {active === item.id && <span style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-primary)' }} />}
            </button>
          ))}

          <div className="sidebar-section" style={{ marginTop: 24 }}>Account</div>
          <button className="sidebar-nav-item" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            Logout
          </button>
        </nav>

        {/* User info */}
        <div className="sidebar-user">
          <div className="sidebar-avatar">
            {(user?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sidebar-user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name || 'User'}
            </div>
            <div className="sidebar-user-role">{user?.role}</div>
          </div>
        </div>
      </div>
    </>
  );
}
